import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import QRCode from 'react-qr-code';
import '../styles/Profile.css';

const icons = {
  email: (
    <svg width="20" height="20" fill="#0072bc" viewBox="0 0 20 20"><path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z"></path><path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z"></path></svg>
  ),
  phone: (
    <svg width="20" height="20" fill="#0072bc" viewBox="0 0 20 20"><path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z"></path></svg>
  ),
  website: (
    <svg width="20" height="20" fill="#0072bc" viewBox="0 0 20 20"><path fill-rule="evenodd" d="M4.083 9h1.946c.089-1.546.383-2.97.837-4.118A6.004 6.004 0 004.083 9zM10 2a8 8 0 100 16 8 8 0 000-16zm0 2c-.076 0-.232.032-.465.262-.238.234-.497.623-.737 1.182-.389.907-.673 2.142-.766 3.556h3.936c-.093-1.414-.377-2.649-.766-3.556-.24-.56-.5-.948-.737-1.182C10.232 4.032 10.076 4 10 4zm3.971 5c-.089-1.546-.383-2.97-.837-4.118A6.004 6.004 0 0115.917 9h-1.946zm-2.003 2H8.032c.093 1.414.377 2.649.766 3.556.24.56.5.948.737 1.182.233.23.389.262.465.262.076 0 .232-.032.465-.262.238-.234.498-.623.737-1.182.389-.907.673-2.142.766-3.556zm1.166 4.118c.454-1.147.748-2.572.837-4.118h1.946a6.004 6.004 0 01-2.783 4.118zm-6.268 0C6.412 13.97 6.118 12.546 6.03 11H4.083a6.004 6.004 0 002.783 4.118z" clip-rule="evenodd"></path></svg>
  ),
  linkedin: (
    <svg width="20" height="20" fill="none" viewBox="0 0 24 24"><path fill="#1976d2" d="M19 0h-14c-2.76 0-5 2.24-5 5v14c0 2.76 2.24 5 5 5h14c2.76 0 5-2.24 5-5v-14c0-2.76-2.24-5-5-5zm-11 19h-3v-9h3v9zm-1.5-10.28c-.97 0-1.75-.79-1.75-1.75s.78-1.75 1.75-1.75 1.75.79 1.75 1.75-.78 1.75-1.75 1.75zm15.5 10.28h-3v-4.5c0-1.08-.02-2.47-1.5-2.47-1.5 0-1.73 1.17-1.73 2.39v4.58h-3v-9h2.89v1.23h.04c.4-.75 1.37-1.54 2.82-1.54 3.01 0 3.57 1.98 3.57 4.56v4.75z" /></svg>
  )
};

const Profile = () => {
  const { email } = useParams();
  const [employee, setEmployee] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchEmployeeProfile = async () => {
      try {
        const response = await fetch(`/api/employees/email/${email}`);
        if (!response.ok) throw new Error('Failed to fetch employee profile');
        const data = await response.json();
        setEmployee(data);
      } catch (err) {
        setError('Failed to fetch employee profile');
      } finally {
        setLoading(false);
      }
    };
    fetchEmployeeProfile();
  }, [email]);

  const handleDownloadVCard = async () => {
    try {
      const response = await fetch(`/api/employees/vcard/${email}`);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${employee.name}.vcf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      setError('Failed to download vCard');
    }
  };

  if (loading) return <div className="loading">Loading...</div>;
  if (error) return <div className="error-message">{error}</div>;
  if (!employee) return <div className="not-found">Employee not found</div>;
  if (employee.status !== 'active') return <div className="not-found">This employee profile is inactive.</div>;

  // vCard QR data
  const vCardData = `BEGIN:VCARD\nVERSION:3.0\nN:${employee.name};;;\nFN:${employee.name}\nTITLE:${employee.title}\nORG:${employee.company}\nEMAIL:${employee.email}\nTEL:${employee.phone}\nADR:;;${employee.address};;;\nURL:${employee.website}\nEND:VCARD`;

  return (
    <div className="profile-vcard-outer-biz">
      <div className="profile-vcard-card-biz">
        <div className="profile-vcard-left-biz">
          <img src="/logo.png" alt="Pricol Logo" className="profile-vcard-logo-biz" />
          <div className="profile-vcard-name-biz">{employee.name}</div>
          <div className="profile-vcard-title-biz">{employee.title}</div>
          <div className="profile-vcard-company-biz">{employee.company}</div>
          <div className="profile-vcard-address-biz">{employee.address}</div>
          <div className="profile-vcard-contact-biz">
            <div className="profile-vcard-contact-item">{icons.email}<a href={`mailto:${employee.email}`}>{employee.email}</a></div>
            <div className="profile-vcard-contact-item">{icons.phone}<a href={`tel:${employee.phone}`}>{employee.phone}</a></div>
            {employee.website && <div className="profile-vcard-contact-item">{icons.website}<a href={employee.website.startsWith('http') ? employee.website : `https://${employee.website}`} target="_blank" rel="noopener noreferrer">{employee.website}</a></div>}
            {employee.linkedin && <div className="profile-vcard-contact-item">{icons.linkedin}<a href={employee.linkedin} target="_blank" rel="noopener noreferrer">{employee.linkedin}</a></div>}
          </div>
          {/* <div className={`profile-vcard-status-biz ${employee.status}`}>{employee.status === 'active' ? 'Active' : 'Inactive'}</div> */}
        </div>
        <div className="profile-vcard-divider-biz">
          <div className="profile-vcard-vertline-biz"></div>
          <div className="profile-vcard-diamond-biz"></div>
        </div>
        <div className="profile-vcard-right-biz">
          <div className="profile-vcard-qr-wrapper-biz">
            <QRCode value={vCardData} size={380} level="H" />
            <img src="/logo.png" alt="Pricol Logo" width="100" height="100" className="profile-vcard-qr-logo-biz" />
          </div>
          <a className="profile-vcard-download-link-biz" onClick={handleDownloadVCard} href="#download">Download Contact (vCard)</a>
        </div>
      </div>
    </div>
  );
};

export default Profile;